import { useMemo } from 'react'
import './NotificationDrawer.css'

function notificationSortValue(n) {
  if (typeof n.sortRank === 'number' && Number.isFinite(n.sortRank)) return n.sortRank
  const tier = n.type === 'property_response' ? 3 : n.type === 'new_message' ? 2 : 1
  return tier * 1e14 + (n.timestamp || 0)
}

export default function NotificationDrawer({ isOpen, onClose, notifications = [], onSelectThread, onClearNotifications, pushLayout = false }) {
  const sortedNotifications = useMemo(
    () => [...notifications].sort((a, b) => notificationSortValue(b) - notificationSortValue(a)),
    [notifications]
  )

  if (!isOpen) return null

  const content = (
    <aside
      className={`notificationDrawer ${pushLayout ? 'notificationDrawer--push' : ''}`}
      role="dialog"
      aria-label="Notifications"
    >
        <div className="notificationDrawerHeader">
          <h2 className="notificationDrawerTitle">Notifications</h2>
          <button
            type="button"
            className="notificationDrawerClose"
            onClick={onClose}
            aria-label="Close notifications"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div className="notificationDrawerContent">
          {notifications.length === 0 ? (
            <div className="notificationDrawerEmpty">
              No notifications yet. New messages, property responses, and AI replies will appear here.
            </div>
          ) : (
            <ul className="notificationDrawerList">
              {sortedNotifications.map((n) => {
                const isPropertyResponse = n.type === 'property_response'
                const isNewMessage = n.type === 'new_message'
                const typeClass = isPropertyResponse
                  ? 'notificationDrawerItem--property'
                  : isNewMessage
                    ? 'notificationDrawerItem--message'
                    : 'notificationDrawerItem--ai'
                const isUnread = !n.read
                const title = isPropertyResponse
                  ? 'Property response received'
                  : isNewMessage
                    ? 'New message in thread'
                    : 'AI responded & archived'
                const icon = isPropertyResponse ? '📬' : isNewMessage ? '💬' : '✓'
                return (
                  <li key={n.id} className={`notificationDrawerItem ${typeClass} ${isUnread ? 'notificationDrawerItem--unread' : ''}`}>
                    <button
                      type="button"
                      className="notificationDrawerItemBtn"
                      onClick={() => onSelectThread?.(n.threadId, n.id)}
                    >
                      {isUnread && <span className="notificationDrawerItemUnreadDot" aria-hidden />}
                      <span className={`notificationDrawerItemIcon ${isPropertyResponse ? 'notificationDrawerItemIcon--property' : isNewMessage ? 'notificationDrawerItemIcon--message' : 'notificationDrawerItemIcon--ai'}`} aria-hidden>
                        {icon}
                      </span>
                      <div className="notificationDrawerItemBody">
                        <span className={`notificationDrawerItemTitle ${isUnread ? 'notificationDrawerItemTitle--unread' : ''}`}>
                          {title}
                        </span>
                        <span className="notificationDrawerItemMeta">
                          {n.guestName || 'Guest'} · {n.propertyName || 'Property'}
                        </span>
                        {isNewMessage && n.preview ? (
                          <span className="notificationDrawerItemPreview">{n.preview}</span>
                        ) : null}
                        <span className="notificationDrawerItemTime">
                          {n.timestamp ? new Date(n.timestamp).toLocaleString() : ''}
                        </span>
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
        {notifications.length > 0 && (
          <div className="notificationDrawerFooter">
            <button
              type="button"
              className="notificationDrawerClearBtn"
              onClick={onClearNotifications}
            >
              Clear notifications
            </button>
          </div>
        )}
      </aside>
  )

  if (pushLayout) {
    return content
  }

  return (
    <>
      <div className="notificationDrawerBackdrop" aria-hidden="true" />
      {content}
    </>
  )
}
